import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mockIncomes } from '@/mocks/tr/incomes';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

export default function Incomes() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  const filtered = useMemo(() => {
    return mockIncomes.filter((i) =>
      i.title.toLowerCase().includes(search.toLowerCase()) ||
      i.description.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('incomes.title')}</h1>
          <p className="text-sm text-boutique-warm mt-1">Satış dışı ekstra gelirleri kaydedin</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {t('incomes.newIncome')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 bg-white p-4 rounded-xl border border-cream-200">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-boutique-warm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-cream-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50 border-b border-cream-200">
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Gelir</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Tutar</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Ödeme Tipi</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Açıklama</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {filtered.map((i) => (
                <tr key={i.id} className="hover:bg-cream-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{i.date}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-boutique-black">{i.title}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600 whitespace-nowrap">{formatCurrency(i.amount)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600 border border-green-200">
                      {i.payment_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-boutique-warm max-w-xs truncate">{i.description}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button className="w-8 h-8 rounded-lg flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title={t('common.edit')}>
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button className="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors" title={t('common.delete')}>
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-coins-line text-4xl text-cream-300 mb-3" />
            <p className="text-sm text-boutique-warm">{t('common.noData')}</p>
          </div>
        )}
      </div>

      {/* New Income Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('incomes.newIncome')}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Gelir Başlığı</label>
                <input className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="Örn: Raf Kiralama" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Tutar</label>
                <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Tarih</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tipi</label>
                <select className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm bg-white">
                  <option>Nakit</option>
                  <option>Kart</option>
                  <option>Havale</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Açıklama</label>
                <textarea className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm resize-none h-20" placeholder="Ek açıklama..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 px-4 border border-cream-200 text-boutique-black rounded-lg text-sm font-medium hover:bg-cream-50 transition-colors">
                  {t('common.cancel')}
                </button>
                <button className="flex-1 py-2.5 px-4 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors">
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}